import AuthController from '../services/AuthController.js';
import { requireRouteCapability } from './routeCapability.js';
import { clearEditorSession, clearEditorReturn, editorWorkspaceEnabled, rememberEditorReturn, getSuspendedEditor, getEditorSession } from './magnetEditorSession.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { showEditorRecovery } from './magnetEditorRecovery.js';

// Shared by the app and the isolated rehearsal: only authentication and the server
// are simulated there, not a second implementation of the route lifecycle.
export function registerMagnetWorkspaceRoutes(router, { checkRouteAccess }) {
  let generation = 0;
  async function load(match, creating = false) {
    const ticket = ++generation;
    const requestedPath = `#/${match.url.replace(/^\//, '')}`;
    const requestedOrg = AuthController.getStoredActiveOrg();
    // Navigo invokes handlers before committing programmatic URL changes.
    await Promise.resolve();
    const current = () => ticket === generation && window.location.hash === requestedPath
      && requestedOrg === AuthController.getStoredActiveOrg();
    if (!current()) return;
    if (!checkRouteAccess('home')) {
      rememberEditorReturn(requestedPath); router.navigate('/signin'); return;
    }
    if (!editorWorkspaceEnabled()) { router.navigate('/magnets'); return; }
    if (showEditorRecovery(document.getElementById('app'), requestedPath.slice(1), () => load(match, creating))) return;
    PubSub.publish(AppBroadcast.ROUTE_NAV_BEFORE, { route: 'magnets' });
    try {
      const scope = await AuthController.fetchScope();
      if (!current()) return;
      if (!checkRouteAccess('home')) {
        rememberEditorReturn(requestedPath); router.navigate('/signin'); return;
      }
      if (!scope || (requestedOrg && scope.activeOrg?.guid !== requestedOrg)) {
        throw new Error('This workspace could not be confirmed. Your editor has not been opened. Try again.');
      }
      const held = getSuspendedEditor();
      if (held && scope.entity?.guid !== held.actor) throw new Error('Sign in with the original account to recover your unfinished work.');
      const path = creating ? '/magnets/new' : match.data.section ? '/magnets/:guid/edit/:section' : '/magnets/:guid/edit';
      if (!requireRouteCapability(path)) return;
      const module = await (creating ? import('../routes/magnet-create-route.js') : import('../routes/magnet-editor-route.js'));
      if (!current()) return;
      const container = document.getElementById('app');
      clearEditorReturn(requestedPath);
      if (creating) await module.mountCreator(container);
      else await module.mountEditor(container, match.data.guid, match.data.section || 'conversation');
      if (current()) PubSub.publish(AppBroadcast.ROUTE_NAV_AFTER, { route: 'magnets' });
    } catch (error) {
      if (!current()) return;
      clearEditorSession();
      const message = document.createElement('p'); message.setAttribute('role', 'alert');
      message.textContent = error.message || 'The editor could not be opened. Try again.';
      const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Try again';
      retry.addEventListener('click', () => load(match, creating));
      document.getElementById('app').replaceChildren(message, retry);
      PubSub.publish(AppBroadcast.ROUTE_NAV_ERROR, { route: 'magnets', error });
    }
  }
  router.on('/magnets/new', match => load(match, true));
  // Section changes use replaceState while retaining the working controls.
  // After publication Navigo can still consider the original editor URL current;
  // its already hook must reopen that URL when the working session has ended.
  const reopen = match => { if (!getEditorSession()) load(match); };
  router.on('/magnets/:guid/edit', match => load(match), { already: reopen });
  router.on('/magnets/:guid/edit/:section', match => load(match), { already: reopen });
}
